import { PermissionUtils } from '@mitsubishi/core';
import {
  PerformanceMessage,
  PriceTypeEnum,
  StudyUnit,
  StudyUnitAccessoryWithPrice,
} from '@mitsubishi/shared/data-access';

import { AccessoryType } from '../enums';

export function unitUtilsFactory() {
  // Injected dependencies must be declared here
  const hasLocalPricePermission = PermissionUtils.hasViewLocalPricePermission();
  const hasSellInPricePermission = PermissionUtils.hasViewSellInPricePermission();
  const hasBasePricePermission = PermissionUtils.hasViewBasePricePermission();
  const hasOriginalPricePermission = PermissionUtils.hasViewOriginalPricePermission();

  return {
    hasPerformanceErrorMessages: (messages: PerformanceMessage[]): boolean => {
      return messages.some(m => m.messageType === 2);
    },
    getStudyUnitPerformanceMessages: (unit: StudyUnit | undefined): PerformanceMessage[] => {
      if (!unit) {
        return [];
      }

      const performances = Object.values(unit.performances);

      return performances.reduce((acc, performance) => {
        const messages = Object.values(performance.performance.messages);

        return [...acc, ...messages];
      }, [] as PerformanceMessage[]);
    },
    getNonStandardAccessories: (unit: StudyUnit): StudyUnitAccessoryWithPrice[] => {
      return Object.values(unit.accessories).filter(accessory => {
        return accessory.type !== AccessoryType.STD;
      });
    },
    getTotalAccessoriesPrice: (
      accessories: StudyUnitAccessoryWithPrice[],
      selectedPrice: PriceTypeEnum | undefined
    ): number => {
      if (!accessories.length) return 0;

      const getPriceForAccessory = (accessory: StudyUnitAccessoryWithPrice) => {
        if (hasLocalPricePermission && selectedPrice === PriceTypeEnum.local) {
          return accessory.prices.local ?? 0;
        }

        if (hasSellInPricePermission && selectedPrice === PriceTypeEnum.sellIn) {
          return accessory.prices.sellIn ?? 0;
        }

        if (hasBasePricePermission && selectedPrice === PriceTypeEnum.basePrice) {
          return accessory.prices.priceList ?? 0;
        }

        // APPLYME-6060: source-currency price aggregate (native price list currency).
        if (hasOriginalPricePermission && selectedPrice === PriceTypeEnum.originalPrice) {
          return accessory.prices.originalPrice ?? 0;
        }

        return 0;
      };

      return accessories.reduce((total, accessory) => total + getPriceForAccessory(accessory), 0);
    },
  };
}
